import { useNavigate, useLocation } from "react-router-dom";
import * as React from "react";
import { AppContext } from "../../setup/context";
import { APIOutput, ContainerRef, DecisionTreeContent, EntityFactory, EntityRef, EntityType, IDecisionTree, PageFactory } from "@amzn/ask-legal-domain";
import { StringUtils } from "../../utils/common-utils";
import { CustomEdge } from "./custom-resources/CustomEdge";
import { CustomNode } from "./custom-resources/CustomNode";
import { NodeEditModal } from "./node-editor/NodeEditModal";
import { ReactFlowProvider } from "reactflow";
import { useLayoutManager } from "../../hooks/decision-tree-node-hook";
import { ConfirmLeavePageModal } from "../common/ConfirmLeavePageModal";
import { BatchedSaveQueue } from "../../utils/decisiontree/save-queue";
import { ReactFlowCanvas } from "./ReactFlowCanvas";
import { useSaveStatus } from "../../hooks/use-save-status";
import { useDecisionTreeRefs } from "../../hooks/use-decision-tree-refs";
import { executeWithRetry } from "../../utils/decisiontree/execute-with-retry";
import { buildGraph } from "../../utils/decisiontree/graph-builder";
import "../../styles/component/decision-tree/editor.scss";
import useRoutedParamsWithContext from "../../setup/useRoutedParamsWithContext";
import { Preference } from "../../setup/preference";
import { ConfirmActionModal } from "./confirm-action-modal/ConfirmActionModal";
import { OperationInProgressNotification } from "./OperationInProgressNotification";
import { OperationDedupTracker } from "../../utils/decisiontree/operation-dedup";
import { deduplicateDeleteSelection } from "../../utils/decisiontree/selection-dedup";
import { classifySubtree } from "../../utils/decisiontree/subtree-classifier";

    /** Typed container data loaded from API */
    interface ContainerData {
        loadedContent: DecisionTreeContent;
        containerMetadata: ContainerRef;
    }

    /** Typed draft API response for type-safe access to id and version */
    interface DraftData {
        id: string;
        version: number;
    }

export namespace DecisionTreeEdit {
    /**
     * Data loading component. Loads container and draft, then renders the editor.
     */
    export const Comp = () => {
        const context = React.useContext(AppContext);
        const { containerId } = useRoutedParamsWithContext();
        const params = new URLSearchParams(useLocation().search);
        const pageDraftId = params.get("draftId");

        const [isLoading, setIsLoading] = React.useState(true);
        const [loadError, setLoadError] = React.useState<string | null>(null);
        const [containerData, setContainerData] = React.useState<ContainerData | null>(null);
        const [pageDraftRef, setPageDraftRef] = React.useState<EntityRef | null>(null);

        React.useEffect(() => {
            const loadAll = async () => {
                try {
                    setIsLoading(true);
                    setLoadError(null);

                    const [containerOutput, draftOutput] = await Promise.all([
                        context.getContainerAPI().load(containerId),
                        context.getPageDraftAPI().load(pageDraftId)
                    ]);

                    const containerApiOutput = APIOutput.fromRaw(containerOutput.data);
                    if (!containerApiOutput.isOk() || !containerApiOutput.data) {
                        throw new Error("Failed to load container data");
                    }
                    setContainerData(containerApiOutput.data as ContainerData);

                    const draftApiOutput = APIOutput.fromRaw(draftOutput.data);
                    if (!draftApiOutput.isOk() || !draftApiOutput.data) {
                        throw new Error("Failed to load draft data");
                    }
                    const draft = draftApiOutput.data as DraftData;
                    setPageDraftRef(EntityFactory.fromEntityAttributes(
                        draft.id, EntityType.PageDraft, draft.version
                    ));

                    setIsLoading(false);
                } catch (err) {
                    console.error("Failed to load data:", err);
                    setLoadError("Failed to load decision tree. Please try refreshing the page.");
                    setIsLoading(false);
                }
            };
            loadAll();
        }, [containerId, pageDraftId]);

        if (loadError) {
            return <div style={{ padding: "20px", color: "red" }}>{loadError}</div>;
        }
        if (isLoading || !containerData || !pageDraftRef) {
            return <div style={{ padding: "20px" }}>Loading decision tree editor...</div>;
        }

        return <ReactFlowEditor
            key={pageDraftRef.id}
            initialContent={containerData.loadedContent}
            pageDraftRef={pageDraftRef}
            containerRef={containerData.containerMetadata}
        />;
    };

    /**
     * Main editor orchestration component.
     * Wires together layout, save queue, drag/edit/toggle handlers.
     */
    export const ReactFlowEditor = (props: {
        pageDraftRef: EntityRef;
        containerRef: ContainerRef;
        initialContent: DecisionTreeContent;
    }) => {
        const context = React.useContext(AppContext);
        const navigate = useNavigate();
        const [editingNodeId, setEditingNodeId] = React.useState<string>("");
        const [rfKey, setRfKey] = React.useState(0);
        const [showDragTip, setShowDragTip] = React.useState(false);
        const dragTipTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

        // --- Confirmation modal state (replaces window.confirm for Cloudscape UI consistency) ---
        const [pendingConfirmation, setPendingConfirmation] = React.useState<{
            header: string;
            message: string;
            description?: string;
            confirmLabel: string;
            alertType?: "warning" | "info";
            onConfirm: () => void;
        } | null>(null);

        // --- Per-operation duplicate prevention ---
        // Tracks which specific operations are currently in progress (e.g., "delete-node-abc123").
        // Different operations are NOT blocked — only the exact same operation is prevented from
        // being triggered again while it's still running. This avoids issues like double-deleting
        // the same node if the user clicks quickly.
        const operationDedup = React.useRef(new OperationDedupTracker());
        const [duplicateAttemptCount, setDuplicateAttemptCount] = React.useState(0);
        // Global guard: only one tree-edit operation at a time (prevents concurrent version conflicts)
        const isEditInFlight = React.useRef(false);

        // --- Core infrastructure ---
        const saveQueue = React.useRef(new BatchedSaveQueue<{x: number, y: number}>({
            debounceMs: 300,
            maxWaitMs: 5000
        }));

        const layoutManager = useLayoutManager({ content: props.initialContent });

        const {
            saveStatus, setSaveStatus, saveType, setSaveType,
            markSaveStarted, markSaveCompleted, markSaveCompletedWithStaleView, markSaveFailed, hasUnsavedChanges
        } = useSaveStatus(saveQueue);

        const {
            currentDraftRef, currentContainerRef, currentContent,
            updateRefsFromOutput, reloadContainerAndUpdateRefs
        } = useDecisionTreeRefs({
            pageDraftRef: props.pageDraftRef,
            containerRef: props.containerRef,
            initialContent: props.initialContent,
            layoutManager,
            saveQueue
        });

        // --- Lab check ---
        const enhancementLabEnabled = context.hasLab(Preference.Lab.DecisionTreeEnhancement);

        // --- Memoized values ---
        const nodeTypes = React.useMemo(() => CustomNode.List, []);
        const edgeTypes = React.useMemo(() => CustomEdge.List, []);
        const rootNodeId = React.useMemo(() => props.initialContent.rootNodeId, [props.initialContent.rootNodeId]);

        // Unified failure fallback: reload from server to resync UI.
        // Shared across drag, layout toggle, and edit operations.
        const reloadOnFailure = React.useCallback(() => { reloadContainerAndUpdateRefs(); }, [reloadContainerAndUpdateRefs]);

        const CONFLICT_ALERT_MESSAGE =
            "Unable to save changes due to concurrent edits.\n\n" +
            "Another user may be editing the same decision tree.\n" +
            "Please refresh the page and try again.";

        // --- Navigation protection ---
        const navigateBack = React.useCallback(() => {
            navigate(`/page/${PageFactory.fromEntityId(props.containerRef.id)}/admin?tab=draftPage&container=${props.containerRef.id}`);
        }, [navigate, props.containerRef.id]);

        const closeEditor = React.useCallback(() => {
            if (hasUnsavedChanges()) {
                setPendingConfirmation({
                    header: "Leave Editor",
                    message: "Changes are still being saved. If you leave now, unsaved changes may be lost.",
                    description: "Are you sure you want to leave?",
                    confirmLabel: "Leave",
                    alertType: "info",
                    onConfirm: () => {
                        setPendingConfirmation(null);
                        navigateBack();
                    }
                });
                return;
            }
            navigateBack();
        }, [hasUnsavedChanges, navigateBack]);

        // Deps: [] is safe — hasUnsavedChanges is a useCallback([saveQueue]) stable ref.
        React.useEffect(() => {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                if (hasUnsavedChanges()) {
                    e.preventDefault();
                    e.returnValue = "";
                }
            };
            window.addEventListener("beforeunload", handleBeforeUnload);
            return () => window.removeEventListener("beforeunload", handleBeforeUnload);
        }, []);

        // --- Drag handlers ---
        const nodeInitialPositions = React.useRef<Map<string, {x: number, y: number}>>(new Map());

        const handleNodeDragStart = (event: any, node: any) => {
            nodeInitialPositions.current.set(node.id, { x: node.position.x, y: node.position.y });
        };

        // Deps: [] is safe — only references saveQueue.current (ref) and setEditingNodeId (React state setter, stable).
        const handleNodeDoubleClick = React.useCallback(async (event: any, node: any) => {
            await saveQueue.current.flush();
            setEditingNodeId(node.id);
        }, []);

        const handleNodeDragStop = (event: any, node: any) => {
            try {
                if (editingNodeId) return;
                if (!layoutManager.useCustomizedLayout) return;

                const initialPos = nodeInitialPositions.current.get(node.id);
                if (initialPos) {
                    const dx = Math.abs(node.position.x - initialPos.x);
                    const dy = Math.abs(node.position.y - initialPos.y);
                    if (dx < 1 && dy < 1) return;
                }

                layoutManager.setNodes(prevNodes =>
                    prevNodes.map(n => n.id === node.id ? { ...n, position: node.position } : n)
                );

                currentContent.current = {
                    ...currentContent.current,
                    allNodes: currentContent.current.allNodes.map(n =>
                        n.id === node.id ? { ...n, position: node.position } : n
                    )
                };

                setSaveStatus("saving");
                setSaveType("drag");
                saveQueue.current.collect(node.id, node.position);
            } finally {
                // Clean up after drag completes, regardless of code path
                nodeInitialPositions.current.delete(node.id);
            }
        };

        // --- Batch flush callback ---
        // Deps: [] is intentional — registered once on mount. All referenced functions inside the
        // closure access .current refs (currentDraftRef, currentContainerRef, saveQueue) which are
        // always fresh, and all callbacks (markSaveStarted, markSaveCompleted, etc.) are useCallback([])
        // stable refs from useSaveStatus/useDecisionTreeRefs.
        React.useEffect(() => {
            saveQueue.current.onFlush(async (updates) => {
                markSaveStarted();
                await executeWithRetry({
                    logPrefix: "[DragSave]",
                    execute: async () => {
                        const batchInput = IDecisionTree.BatchUpdateInput.create({
                            draftRef: currentDraftRef.current,
                            containerId: currentContainerRef.current.id,
                            updates: Array.from(updates.entries()).map(([nodeId, position]) => ({
                                nodeId, position
                            }))
                        });
                        const rawOutput = await context.getDecisionTreeNodeAPI().batchUpdate(batchInput);
                        return APIOutput.fromRaw(rawOutput.data);
                    },
                    onSuccess: (data) => updateRefsFromOutput(data as IDecisionTree.BatchUpdateOutput),
                    onBeforeRetry: reloadContainerAndUpdateRefs,
                    onComplete: markSaveCompleted,
                    onFailed: markSaveFailed,
                    onConflictExhausted: reloadOnFailure,
                    onNonRetryableError: reloadOnFailure,
                    conflictAlertMessage: CONFLICT_ALERT_MESSAGE
                });
            });
        }, []);

        // --- Layout toggle handler ---
        const handleLayoutToggle = async (checked: boolean) => {
            const updatedContent = { ...currentContent.current, useCustomizedLayout: checked };
            currentContent.current = updatedContent;
            layoutManager.update(updatedContent);
            setRfKey(prev => prev + 1);

            if (checked) {
                setShowDragTip(true);
                if (dragTipTimerRef.current) clearTimeout(dragTipTimerRef.current);
                dragTipTimerRef.current = setTimeout(() => {
                    setShowDragTip(false);
                    dragTipTimerRef.current = null;
                }, 5000);
            }

            setSaveStatus("saving");
            setSaveType("layout");

            saveQueue.current.enqueue(
                async () => {
                    markSaveStarted();
                    await executeWithRetry({
                        logPrefix: "[LayoutToggle]",
                        execute: async () => {
                            const updateInput = IDecisionTree.UpdateDraftInput.create({
                                draftRef: currentDraftRef.current,
                                containerId: currentContainerRef.current.id,
                                nodeId: props.initialContent.rootNodeId,
                                useCustomizedLayout: checked
                            });
                            const rawOutput = await context.getDecisionTreeNodeAPI().update(updateInput);
                            return APIOutput.fromRaw(rawOutput.data);
                        },
                        onSuccess: (data) => updateRefsFromOutput(data as IDecisionTree.UpdateDraftOutput),
                        onBeforeRetry: reloadContainerAndUpdateRefs,
                        onComplete: markSaveCompleted,
                        onFailed: markSaveFailed,
                        onConflictExhausted: reloadOnFailure,
                        onNonRetryableError: reloadOnFailure,
                        conflictAlertMessage: CONFLICT_ALERT_MESSAGE
                    });
                },
                "layout-toggle",
                true
            ).catch(error => {
                console.error("[LayoutToggle] Queue operation failed:", error);
                markSaveFailed();
            });
        };

        // --- Orphan node count (uses buildGraph for consistency) ---
        const orphanNodeCount = React.useMemo(() => {
            const content = layoutManager.contentRef.current;
            if (!content?.allNodes || !content.rootNodeId) return 0;
            return buildGraph(content).orphanCount;
        }, [layoutManager.nodes]); // Recompute when nodes change

        // --- Debounced background reload: fires once after rapid operations settle ---
        const pendingReloadTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

        const scheduleBackgroundReload = React.useCallback((logPrefix: string) => {
            // Cancel any pending reload — only the last one runs
            if (pendingReloadTimer.current) clearTimeout(pendingReloadTimer.current);
            pendingReloadTimer.current = setTimeout(async () => {
                pendingReloadTimer.current = null;
                // Only reload if no operations are in flight
                if (isEditInFlight.current) return;
                if (saveQueue.current?.hasPending() || (saveQueue.current?.getQueueLength() ?? 0) > 0) return;
                const success = await reloadContainerAndUpdateRefs();
                if (!success) console.warn(`${logPrefix} Background reload failed — UI may be slightly stale`);
            }, 1000); // 1s debounce — waits for rapid operations to settle
        }, [reloadContainerAndUpdateRefs]);

        // --- Helper: Apply API output optimistically + schedule background reload ---
        // Version refs (draftRef, containerRef) are updated immediately from the API response.
        // This eliminates the race condition where a queued reload hadn't completed before the
        // next operation read stale version refs (causing "version not found" errors).
        const applyOutputAndReload = React.useCallback((
            logPrefix: string,
            output: IDecisionTree.UpdateDraftOutput
        ) => {
            // Optimistic update: immediately apply content + version refs
            if (output.content) {
                currentContent.current = output.content;
                layoutManager.update(output.content);
            }
            if (output.updatedDraftRef) {
                currentDraftRef.current = output.updatedDraftRef;
            }
            if (output.updatedContainerRef) {
                currentContainerRef.current = output.updatedContainerRef;
            }

            // Mark as completed immediately — the API succeeded and refs are up-to-date
            markSaveCompleted();

            // Schedule a debounced background reload for full UI consistency
            // (positions, edge routing, etc.) — doesn't block the next operation
            scheduleBackgroundReload(logPrefix);
        }, [layoutManager.update, markSaveCompleted, scheduleBackgroundReload]);

        /**
         * Shared helper for tree-edit handler callbacks (RemoveLink, DeleteNode, CopyNode, etc.).
         *
         * Encapsulates the common pattern: check duplicate → flush queue → set save state →
         * execute API call → apply optimistic update + reload → handle errors with fallback reload.
         *
         * Includes per-operation duplicate prevention: if the same operationKey is already in progress,
         * the call is skipped and a toast notification is shown. Different operations are NOT blocked.
         * Drag/position operations are not affected (they use the batched save queue directly).
         *
         * @param logPrefix - Label for console logs (e.g., "[RemoveLink]")
         * @param operationKey - Unique key identifying this specific operation (e.g., "delete-node-abc123")
         * @param execute - Async function that performs the API call and returns the raw output
         */
        const executeTreeEdit = React.useCallback(async (
            logPrefix: string,
            operationKey: string,
            execute: () => Promise<{ data: any }>
        ): Promise<boolean> => {
            // --- Global serialization: block if any edit operation is in flight ---
            if (isEditInFlight.current) {
                console.warn(`${logPrefix} Skipped — another edit operation is in flight`);
                setDuplicateAttemptCount(c => c + 1);
                return false;
            }

            // --- Per-operation duplicate prevention ---
            if (!operationDedup.current.tryAcquire(operationKey)) {
                console.warn(`${logPrefix} Skipped — operation "${operationKey}" is already in progress`);
                setDuplicateAttemptCount(c => c + 1);
                return false;
            }

            isEditInFlight.current = true;
            try {
                await saveQueue.current.flush();
                setSaveStatus("saving");
                setSaveType("edit");
                markSaveStarted();

                const rawOutput = await execute();
                const output = APIOutput.fromRaw<IDecisionTree.UpdateDraftOutput>(rawOutput.data);

                if (output.isErr()) {
                    console.error(`${logPrefix} API error:`, output.err?.message);
                    markSaveFailed();
                    return false;
                }

                console.log(`${logPrefix} Success`);
                applyOutputAndReload(logPrefix, output.data);
                return true;
            } catch (e: any) {
                console.error(`${logPrefix} Exception:`, e);
                markSaveFailed();
                reloadContainerAndUpdateRefs();
                return false;
            } finally {
                isEditInFlight.current = false;
                operationDedup.current.release(operationKey);
            }
        }, [applyOutputAndReload, reloadContainerAndUpdateRefs, markSaveStarted, markSaveFailed]);

        // --- Context menu: Remove Link (edge right-click) ---
        // Removes the parent→child reference. The child node is preserved as an orphan.
        const handleRemoveLink = React.useCallback((sourceNodeId: string, targetNodeId: string, answerText: string) => {
            const truncatedAnswer = answerText.length > 40 ? answerText.substring(0, 40) + "…" : answerText;
            setPendingConfirmation({
                header: "Remove Link",
                message: `Remove link "${truncatedAnswer}"?`,
                description: "The child node will be disconnected from its parent and become unlinked. It can be reconnected later.",
                confirmLabel: "Remove",
                alertType: "warning",
                onConfirm: async () => {
                    setPendingConfirmation(null);

                    const parentNode = currentContent.current.allNodes.find(n => n.id === sourceNodeId);
                    if (!parentNode) {
                        console.error(`[RemoveLink] Parent node ${sourceNodeId} not found`);
                        return;
                    }

                    const updatedChildren = parentNode.children.filter(c => c.nodeId !== targetNodeId);

                    await executeTreeEdit("[RemoveLink]", `remove-link-${sourceNodeId}-${targetNodeId}`, () => {
                        const updateInput = IDecisionTree.UpdateDraftInput.create({
                            draftRef: currentDraftRef.current,
                            containerId: currentContainerRef.current.id,
                            nodeId: sourceNodeId,
                            children: updatedChildren
                        });
                        return context.getDecisionTreeNodeAPI().update(updateInput);
                    });
                }
            });
        }, [executeTreeEdit]);

        // --- Context menu: Delete Node (node right-click) ---
        const handleDeleteNode = React.useCallback((nodeId: string) => {
            if (nodeId === rootNodeId) return; // Safety check

            const node = currentContent.current.allNodes.find(n => n.id === nodeId);
            const nodeLabel = node?.overviewText || nodeId;
            const truncatedLabel = nodeLabel.length > 30 ? nodeLabel.substring(0, 30) + "…" : nodeLabel;
            const hasChildren = node && node.children.length > 0;

            const message = hasChildren
                ? `Delete node "${truncatedLabel}"? This node has ${node.children.length} child link(s).`
                : `Delete node "${truncatedLabel}"?`;

            const description = hasChildren
                ? "Children will become unlinked. This action cannot be undone."
                : "This will permanently remove the node and all parent references to it. This action cannot be undone.";

            setPendingConfirmation({
                header: "Delete Node",
                message,
                description,
                confirmLabel: "Delete",
                alertType: "warning",
                onConfirm: async () => {
                    setPendingConfirmation(null);

                    await executeTreeEdit("[DeleteNode]", `delete-node-${nodeId}`, () => {
                        const input = IDecisionTree.DeleteNodeInput.create({
                            draftRef: currentDraftRef.current,
                            containerId: currentContainerRef.current.id,
                            nodeId
                        });
                        return context.getDecisionTreeNodeAPI().deleteNode(input);
                    });
                }
            });
        }, [rootNodeId, executeTreeEdit]);

        // --- Clipboard state for copy-paste workflow ---
        // Copy stores the source info in local state (no API call).
        // Paste triggers the actual copyNode API with the target position.
        const [clipboard, setClipboard] = React.useState<{
            type: "node" | "subtree";
            sourceNodeId: string;
        } | null>(null);

        // --- Copy to clipboard (no API call — instant) ---
        const handleCopy = React.useCallback((nodeId: string, includeSubtree: boolean) => {
            const type = includeSubtree ? "subtree" : "node";
            setClipboard({ type, sourceNodeId: nodeId });
            console.log(`[Copy${includeSubtree ? "Subtree" : "Node"}] Copied ${type} ${nodeId} to clipboard`);
        }, []);
        const handleCopyNode = React.useCallback((nodeId: string) => handleCopy(nodeId, false), [handleCopy]);
        const handleCopySubtree = React.useCallback((nodeId: string) => handleCopy(nodeId, true), [handleCopy]);

        // Guard against rapid double-paste before React re-renders with null clipboard
        const isPasting = React.useRef(false);

        // --- Paste from clipboard (calls API with position, then auto-clears clipboard) ---
        const handlePaste = React.useCallback(async (position: { x: number; y: number }) => {
            if (!clipboard || isPasting.current) return;

            isPasting.current = true;
            const logPrefix = clipboard.type === "subtree" ? "[PasteSubtree]" : "[PasteNode]";
            // Capture clipboard data — don't clear yet, isPasting ref guards against double-paste
            const clipboardData = clipboard;

            try {
                const executed = await executeTreeEdit(
                    logPrefix,
                    `paste-${clipboardData.type}-${clipboardData.sourceNodeId}-${Math.round(position.x)}-${Math.round(position.y)}`,
                    () => {
                        const input = IDecisionTree.CopyNodeInput.create({
                            draftRef: currentDraftRef.current,
                            containerId: currentContainerRef.current.id,
                            sourceNodeId: clipboardData.sourceNodeId,
                            includeSubtree: clipboardData.type === "subtree",
                            position
                        });
                        return context.getDecisionTreeNodeAPI().copyNode(input);
                    }
                );
                // Clear clipboard only when paste actually executed and succeeded
                if (executed) setClipboard(null);
            } finally {
                isPasting.current = false;
            }
        }, [clipboard, executeTreeEdit]);

        // --- Drag-to-link: Add Link handler ---
        // Adds a new child reference to the source node's children array.
        const handleAddLink = React.useCallback(async (sourceNodeId: string, targetNodeId: string, answerText: string) => {
            const sourceNode = currentContent.current.allNodes.find(n => n.id === sourceNodeId);
            if (!sourceNode) {
                console.error(`[AddLink] Source node ${sourceNodeId} not found`);
                return;
            }

            // Safety check — should be prevented by isValidConnection
            if (sourceNode.children.some(c => c.nodeId === targetNodeId)) {
                console.warn(`[AddLink] Link ${sourceNodeId} → ${targetNodeId} already exists, skipping`);
                return;
            }

            const updatedChildren = [...sourceNode.children, { nodeId: targetNodeId, answer: answerText }];

            await executeTreeEdit("[AddLink]", `add-link-${sourceNodeId}-${targetNodeId}`, () => {
                const updateInput = IDecisionTree.UpdateDraftInput.create({
                    draftRef: currentDraftRef.current,
                    containerId: currentContainerRef.current.id,
                    nodeId: sourceNodeId,
                    children: updatedChildren
                });
                return context.getDecisionTreeNodeAPI().update(updateInput);
            });
        }, [executeTreeEdit]);

        // --- Edge reconnection (relink): drag edge endpoint to a different node ---
        // Handles two cases:
        //   1. Target changed (same source, different target): update source node's children array
        //      to replace oldTargetId with newTargetId — single API call.
        //   2. Source changed (different source, same or different target): add link to new source first,
        //      then remove link from old source — two sequential API calls (add-first is safer:
        //      partial failure leaves a temporary duplicate rather than data loss).
        // The answer text from the original edge is always preserved.
        const handleReconnectEdge = React.useCallback(async (
            oldSourceId: string, oldTargetId: string,
            newSourceId: string, newTargetId: string,
            answerText: string
        ) => {
            const sourceChanged = oldSourceId !== newSourceId;
            const targetChanged = oldTargetId !== newTargetId;

            if (!sourceChanged && !targetChanged) return; // No-op

            if (!sourceChanged && targetChanged) {
                // Case 1: Only target changed — update the source node's children array
                // Replace oldTargetId with newTargetId, preserving the answer text
                const sourceNode = currentContent.current.allNodes.find(n => n.id === oldSourceId);
                if (!sourceNode) {
                    console.error(`[ReconnectEdge] Source node ${oldSourceId} not found`);
                    return;
                }

                const updatedChildren = sourceNode.children.map(c =>
                    c.nodeId === oldTargetId ? { ...c, nodeId: newTargetId } : c
                );

                // --- Content-level optimistic update: immediately reflect the relink in the content model ---
                // This prevents the "snap back" flash that occurs when executeTreeEdit's internal flush()
                // triggers a layout rebuild from stale content BEFORE the API responds.
                // By updating currentContent + layoutManager FIRST, any rebuild uses the new edge state.
                const snapshotContent = currentContent.current;
                const optimisticContent = {
                    ...snapshotContent,
                    allNodes: snapshotContent.allNodes.map(n =>
                        n.id === oldSourceId ? { ...n, children: updatedChildren } : n
                    )
                };
                currentContent.current = optimisticContent;
                layoutManager.update(optimisticContent);

                const success = await executeTreeEdit("[ReconnectEdge:target]", `reconnect-target-${oldSourceId}-${oldTargetId}-${newTargetId}`, () => {
                    const updateInput = IDecisionTree.UpdateDraftInput.create({
                        draftRef: currentDraftRef.current,
                        containerId: currentContainerRef.current.id,
                        nodeId: oldSourceId,
                        children: updatedChildren
                    });
                    return context.getDecisionTreeNodeAPI().update(updateInput);
                });
                if (!success) {
                    currentContent.current = snapshotContent;
                    layoutManager.update(snapshotContent);
                }
            } else {
                // Case 2: Source changed (and possibly target too)
                // Two-step operation: ADD new link first, then REMOVE old link.
                //
                // Order rationale (Add-first is safer than Remove-first):
                // - If step 1 (add) fails: no change, abort cleanly.
                // - If step 1 (add) succeeds but step 2 (remove) fails: temporary duplicate
                //   edge exists (both old A→B and new C→B). This is a benign state — the user
                //   can manually remove the old link. Much safer than Remove-first where
                //   failure leaves the link permanently deleted (data loss).

                // Capture both nodes' children BEFORE any API calls mutate currentContent.
                // executeTreeEdit's applyOutputAndReload replaces currentContent.current with
                // the API response, so reading after step 1 would get post-mutation data.
                const newSourceNode = currentContent.current.allNodes.find(n => n.id === newSourceId);
                if (!newSourceNode) {
                    console.error(`[ReconnectEdge] New source node ${newSourceId} not found`);
                    return;
                }
                const oldSourceNode = currentContent.current.allNodes.find(n => n.id === oldSourceId);
                if (!oldSourceNode) {
                    console.error(`[ReconnectEdge] Old source node ${oldSourceId} not found`);
                    return;
                }

                // Pre-compute both children arrays from the pre-mutation snapshot
                const addedChildren = [...newSourceNode.children, { nodeId: newTargetId, answer: answerText }];
                const removedChildren = oldSourceNode.children.filter(c => c.nodeId !== oldTargetId);

                // --- Content-level optimistic update: reflect the full relink before any API calls ---
                // Same pattern as Case 1 — prevents snap-back flash during flush().
                // If step 1 fails, we roll back to the pre-mutation snapshot.
                const snapshotContent = currentContent.current;
                const optimisticContent = {
                    ...snapshotContent,
                    allNodes: snapshotContent.allNodes.map(n => {
                        if (n.id === newSourceId) return { ...n, children: addedChildren };
                        if (n.id === oldSourceId) return { ...n, children: removedChildren };
                        return n;
                    })
                };
                currentContent.current = optimisticContent;
                layoutManager.update(optimisticContent);

                // Step 1: Add the link to the new source node
                const addSuccess = await executeTreeEdit(
                    "[ReconnectEdge:add-to-new]",
                    `reconnect-add-${newSourceId}-${newTargetId}`,
                    () => {
                        const updateInput = IDecisionTree.UpdateDraftInput.create({
                            draftRef: currentDraftRef.current,
                            containerId: currentContainerRef.current.id,
                            nodeId: newSourceId,
                            children: addedChildren
                        });
                        return context.getDecisionTreeNodeAPI().update(updateInput);
                    }
                );

                if (!addSuccess) {
                    console.error("[ReconnectEdge] Failed to add link to new source — aborting (no changes made)");
                    // Roll back optimistic update — restore pre-mutation content
                    currentContent.current = snapshotContent;
                    layoutManager.update(snapshotContent);
                    return;
                }

                // Step 2: Remove the link from the old source node
                // Uses pre-computed removedChildren from the pre-mutation snapshot to avoid
                // stale reads after step 1's applyOutputAndReload mutated currentContent.

                const removeSuccess = await executeTreeEdit(
                    "[ReconnectEdge:remove-from-old]",
                    `reconnect-remove-${oldSourceId}-${oldTargetId}`,
                    () => {
                        const updateInput = IDecisionTree.UpdateDraftInput.create({
                            draftRef: currentDraftRef.current,
                            containerId: currentContainerRef.current.id,
                            nodeId: oldSourceId,
                            children: removedChildren
                        });
                        return context.getDecisionTreeNodeAPI().update(updateInput);
                    }
                );

                if (!removeSuccess) {
                    // Step 2 failed — new link exists but old link wasn't removed.
                    // This creates a temporary duplicate edge (both A→B and C→B exist).
                    // This is a benign state: the user can manually remove the old link.
                    // Much safer than the alternative (data loss from remove-first).
                    console.error("[ReconnectEdge] Step 2 failed — new link was added but old link remains. User can manually remove the old link.");
                }
            }
        }, [executeTreeEdit, layoutManager]);

        // --- Context menu: Edit Answer (edge right-click) ---
        const handleEditAnswer = React.useCallback(async (sourceNodeId: string, targetNodeId: string, oldAnswer: string, newAnswer: string) => {
            if (newAnswer === oldAnswer) return; // No change

            const parentNode = currentContent.current.allNodes.find(n => n.id === sourceNodeId);
            if (!parentNode) {
                console.error(`[EditAnswer] Parent node ${sourceNodeId} not found`);
                return;
            }

            const updatedChildren = parentNode.children.map(c =>
                c.nodeId === targetNodeId ? { ...c, answer: newAnswer } : c
            );

            await executeTreeEdit("[EditAnswer]", `edit-answer-${sourceNodeId}-${targetNodeId}`, () => {
                const updateInput = IDecisionTree.UpdateDraftInput.create({
                    draftRef: currentDraftRef.current,
                    containerId: currentContainerRef.current.id,
                    nodeId: sourceNodeId,
                    children: updatedChildren
                });
                return context.getDecisionTreeNodeAPI().update(updateInput);
            });
        }, [executeTreeEdit]);

        // --- Unified bulk delete: Delete selected nodes and/or remove selected links ---
        // Uses a single batchDelete API call for atomic deletion.
        // Smart dedup: filters out redundant link removals when the link's target or source
        // is also being deleted (server-side node deletion already cleans up parent references).
        const handleDeleteSelected = React.useCallback((params: {
            nodeIds: string[];
            edges: Array<{ sourceNodeId: string; targetNodeId: string; answerText: string }>;
        }) => {
            // Extra safety: filter out root node
            const deletableNodeIds = params.nodeIds.filter(id => id !== rootNodeId);
            if (deletableNodeIds.length === 0 && params.edges.length === 0) return;

            // --- Smart dedup: filter out redundant link removals ---
            const deduped = deduplicateDeleteSelection(deletableNodeIds, params.edges);
            const dedupedEdges = deduped.edges;

            if (deduped.removedCount > 0) {
                console.log(`[DeleteSelected] Smart dedup: filtered out ${deduped.removedCount} redundant link removal(s)`);
            }

            // After dedup, check if there's still anything to do
            if (deletableNodeIds.length === 0 && dedupedEdges.length === 0) return;

            // Build confirmation message
            const parts: string[] = [];
            if (deletableNodeIds.length > 0) {
                parts.push(`${deletableNodeIds.length} node${deletableNodeIds.length !== 1 ? "s" : ""}`);
            }
            if (dedupedEdges.length > 0) {
                parts.push(`${dedupedEdges.length} link${dedupedEdges.length !== 1 ? "s" : ""}`);
            }
            const message = `Delete ${parts.join(" and ")}?`;

            const descriptions: string[] = [];
            if (deletableNodeIds.length > 0) {
                descriptions.push("Children of deleted nodes will be unlinked.");
            }
            if (dedupedEdges.length > 0) {
                descriptions.push("Child nodes will be disconnected but can be reconnected later. The deleted link(s) cannot be restored.");
            }
            descriptions.push("This action cannot be undone.");

            setPendingConfirmation({
                header: "Delete Selected",
                message,
                description: descriptions.join(" "),
                confirmLabel: `Delete ${parts.join(" and ")}`,
                alertType: "warning",
                onConfirm: async () => {
                    setPendingConfirmation(null);

                    // Build links array for the API (only non-redundant links)
                    const links = dedupedEdges.map(e => ({
                        sourceNodeId: e.sourceNodeId,
                        targetNodeId: e.targetNodeId
                    }));

                    // Build operation key for dedup tracking
                    const keyParts: string[] = [];
                    if (deletableNodeIds.length > 0) {
                        keyParts.push(`nodes:${deletableNodeIds.sort().join(",")}`);
                    }
                    if (links.length > 0) {
                        keyParts.push(`links:${links.map(l => `${l.sourceNodeId}-${l.targetNodeId}`).sort().join(",")}`);
                    }

                    // Single atomic API call to delete all nodes and remove all links at once
                    await executeTreeEdit(
                        `[BatchDelete:${deletableNodeIds.length}nodes,${links.length}links]`,
                        `batch-delete-${keyParts.join(";")}`,
                        () => {
                            const input = IDecisionTree.BatchDeleteInput.create({
                                draftRef: currentDraftRef.current,
                                containerId: currentContainerRef.current.id,
                                ...(deletableNodeIds.length > 0 ? { nodeIds: deletableNodeIds } : {}),
                                ...(links.length > 0 ? { links } : {})
                            });
                            return context.getDecisionTreeNodeAPI().batchDelete(input);
                        }
                    );
                }
            });
        }, [rootNodeId, executeTreeEdit]);

        // --- Delete Subtree (Option B): delete exclusively-owned descendants, preserve shared ones ---
        // Uses two-phase approach to correctly handle diamond/DAG patterns:
        // Phase 1: BFS collects all reachable descendants as deletion candidates
        // Phase 2: Iteratively preserve nodes that have a surviving parent outside the candidate set
        const handleDeleteSubtree = React.useCallback((nodeId: string) => {
            if (nodeId === rootNodeId) return;

            const { deletable: deletableIds, preserved: preservedIds } = classifySubtree(nodeId, currentContent.current.allNodes);
            const nodeMap = new Map(currentContent.current.allNodes.map(n => [n.id, n]));

            if (deletableIds.length === 0) return;

            const rootNode = nodeMap.get(nodeId);
            const nodeLabel = rootNode?.overviewText || nodeId;
            const truncatedLabel = nodeLabel.length > 30 ? nodeLabel.substring(0, 30) + "…" : nodeLabel;

            const descendantCount = deletableIds.length - 1; // Exclude the selected node itself
            let message = descendantCount > 0
                ? `Delete "${truncatedLabel}" and ${descendantCount} descendant node${descendantCount !== 1 ? "s" : ""}?`
                : `Delete "${truncatedLabel}"?`;

            const descriptions: string[] = [];
            descriptions.push(`${deletableIds.length} node${deletableIds.length !== 1 ? "s" : ""} will be permanently deleted.`);
            if (preservedIds.length > 0) {
                descriptions.push(`${preservedIds.length} shared node${preservedIds.length !== 1 ? "s" : ""} will be preserved (connected to other paths).`);
            }
            descriptions.push("This action cannot be undone.");

            setPendingConfirmation({
                header: "Delete Node & All Below",
                message,
                description: descriptions.join(" "),
                confirmLabel: `Delete ${deletableIds.length} node${deletableIds.length !== 1 ? "s" : ""}`,
                alertType: "warning",
                onConfirm: async () => {
                    setPendingConfirmation(null);
                    await executeTreeEdit(
                        `[DeleteSubtree:${deletableIds.length}del,${preservedIds.length}preserved]`,
                        `delete-subtree-${nodeId}`,
                        () => {
                            const input = IDecisionTree.BatchDeleteInput.create({
                                draftRef: currentDraftRef.current,
                                containerId: currentContainerRef.current.id,
                                nodeIds: deletableIds
                            });
                            return context.getDecisionTreeNodeAPI().batchDelete(input);
                        }
                    );
                }
            });
        }, [rootNodeId, executeTreeEdit]);

        // Deps: [] is safe — only reads currentContent.current (ref, always fresh).
        const getContentForValidation = React.useCallback(() => {
            return currentContent.current;
        }, []);

        // NOTE: Unlike other handlers (RemoveLink, DeleteNode, CopyNode, etc.), handleCreateOrphanNode
        // does NOT use executeTreeEdit because it needs executeWithRetry for automatic conflict retry
        // (the node creation is idempotent-safe) and enqueues via saveQueue for proper serialization,
        // whereas executeTreeEdit does a direct flush-then-execute pattern without retry.
        //
        // Deps: [] is safe — all referenced values are either refs (.current) or stable useCallback([])
        // functions: saveQueue.current, currentDraftRef.current, currentContainerRef.current,
        // markSaveStarted, markSaveCompleted, markSaveCompletedWithStaleView, markSaveFailed,
        // updateRefsFromOutput, reloadContainerAndUpdateRefs, reloadOnFailure, context.
        // setSaveStatus/setSaveType are React state setters (inherently stable).
        const handleCreateOrphanNode = React.useCallback(async () => {
            setSaveStatus("saving");
            setSaveType("edit");

            saveQueue.current.enqueue(
                async () => {
                    markSaveStarted();
                    await executeWithRetry({
                        logPrefix: "[CreateOrphanNode]",
                        execute: async () => {
                            const createInput = IDecisionTree.CreateOrphanNodeInput.create({
                                draftRef: currentDraftRef.current,
                                containerId: currentContainerRef.current.id,
                                overviewText: "New Node"
                            });
                            const rawOutput = await context.getDecisionTreeNodeAPI().createOrphanNode(createInput);
                            return APIOutput.fromRaw(rawOutput.data);
                        },
                        onSuccess: (data) => updateRefsFromOutput(data as IDecisionTree.UpdateDraftOutput),
                        onBeforeRetry: reloadContainerAndUpdateRefs,
                        onComplete: async () => {
                            const reloadSuccess = await reloadContainerAndUpdateRefs();
                            if (reloadSuccess) {
                                markSaveCompleted();
                            } else {
                                markSaveCompletedWithStaleView();
                            }
                        },
                        onFailed: markSaveFailed,
                        onConflictExhausted: reloadOnFailure,
                        onNonRetryableError: reloadOnFailure,
                        conflictAlertMessage: CONFLICT_ALERT_MESSAGE
                    });
                },
                "create-orphan-node",
                true
            ).catch(error => {
                console.error("[CreateOrphanNode] Queue operation failed:", error);
                markSaveFailed();
            });
        }, []);

        // Cleanup on unmount — deps: [] is correct (only refs).
        React.useEffect(() => {
            return () => {
                if (dragTipTimerRef.current) clearTimeout(dragTipTimerRef.current);
                if (pendingReloadTimer.current) clearTimeout(pendingReloadTimer.current);
                saveQueue.current.destroy();
            };
        }, []);

        // --- Render ---
        return <>
            <div style={{ width: "auto", height: "87vh", border: "solid", position: "relative" }}>
                <ReactFlowProvider>
                    <ReactFlowCanvas
                        rfKey={rfKey}
                        layoutManager={layoutManager}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodeDoubleClick={handleNodeDoubleClick}
                        onNodeDragStart={handleNodeDragStart}
                        onNodeDragStop={handleNodeDragStop}
                        rootNodeId={rootNodeId}
                        showDragTip={showDragTip}
                        saveStatus={saveStatus}
                        saveType={saveType}
                        closeEditor={closeEditor}
                        handleLayoutToggle={handleLayoutToggle}
                        enhancementLabEnabled={enhancementLabEnabled}
                        // --- Enhanced features (lab-gated) ---
                        onCreateOrphanNode={enhancementLabEnabled ? handleCreateOrphanNode : undefined}
                        orphanNodeCount={enhancementLabEnabled ? orphanNodeCount : undefined}
                        onRemoveLink={enhancementLabEnabled ? handleRemoveLink : undefined}
                        onDeleteNode={enhancementLabEnabled ? handleDeleteNode : undefined}
                        onCopyNode={enhancementLabEnabled ? handleCopyNode : undefined}
                        onCopySubtree={enhancementLabEnabled ? handleCopySubtree : undefined}
                        onAddLink={enhancementLabEnabled ? handleAddLink : undefined}
                        getContentForValidation={enhancementLabEnabled ? getContentForValidation : undefined}
                        onEditAnswer={enhancementLabEnabled ? handleEditAnswer : undefined}
                        // --- Multi-select bulk operations (lab-gated, unified single API call) ---
                        onDeleteSelected={enhancementLabEnabled ? handleDeleteSelected : undefined}
                        onDeleteSubtree={enhancementLabEnabled ? handleDeleteSubtree : undefined}
                        // --- Clipboard-based copy-paste (lab-gated) ---
                        clipboard={enhancementLabEnabled ? clipboard : undefined}
                        onPaste={enhancementLabEnabled ? handlePaste : undefined}
                        onClearClipboard={enhancementLabEnabled ? (() => setClipboard(null)) : undefined}
                        // --- Edge reconnection / relink (lab-gated) ---
                        onReconnectEdge={enhancementLabEnabled ? handleReconnectEdge : undefined}
                    />
                </ReactFlowProvider>
            </div>
            {!StringUtils.isEmpty(editingNodeId) && <>
                <ConfirmLeavePageModal showModal={true} />
                <NodeEditModal
                    draftRef={currentDraftRef.current}
                    nodeId={editingNodeId}
                    containerId={currentContainerRef.current.id}
                    content={currentContent.current}
                    onDismiss={() => {
                        setEditingNodeId("");
                        setSaveType(null);
                    }}
                    onSucceed={(output: IDecisionTree.UpdateDraftOutput) => {
                        setEditingNodeId("");
                        markSaveStarted();
                        setSaveType("edit");

                        // OPTIMISTIC UPDATE: Immediately apply output.content to the layout
                        // so new edges/nodes appear instantly without waiting for reload.
                        // This fixes the "Link to Existing → Orphan" edge-not-rendering bug
                        // caused by data staleness in the async reload path.
                        if (output.content) {
                            currentContent.current = output.content;
                            layoutManager.update(output.content);
                        }

                        // Update version refs immediately (no queue needed since edit already saved)
                        if (output.updatedDraftRef) {
                            currentDraftRef.current = output.updatedDraftRef;
                        }
                        if (output.updatedContainerRef) {
                            currentContainerRef.current = output.updatedContainerRef;
                        }

                        saveQueue.current.enqueue(
                            async () => {
                                // Reload from server for full consistency (positions, versions, etc.)
                                let reloadSuccess = false;
                                for (let attempt = 1; attempt <= 3; attempt++) {
                                    reloadSuccess = await reloadContainerAndUpdateRefs();
                                    if (reloadSuccess) break;
                                    console.warn(`[DecisionTreeEdit] [EDIT] Reload attempt ${attempt}/3 failed, retrying...`);
                                    await new Promise(resolve => setTimeout(resolve, attempt * 500));
                                }
                                if (reloadSuccess) {
                                    markSaveCompleted();
                                } else {
                                    // Edit saved on server but UI refresh failed after 3 attempts.
                                    // Optimistic update already shows correct data, so this is cosmetic.
                                    console.error("[DecisionTreeEdit] [EDIT] Reload failed after 3 attempts — edit saved but UI may be stale");
                                    markSaveCompletedWithStaleView();
                                }
                            },
                            `edit-node-${editingNodeId}`
                        ).catch(async (error) => {
                            console.error("[DecisionTreeEdit] [EDIT] Post-edit processing failed:", error);
                            // Edit succeeded on server but post-processing threw.
                            // Reload to sync UI, then show "stale" (not "error") since the edit IS saved.
                            await reloadContainerAndUpdateRefs();
                            markSaveCompletedWithStaleView();
                        });
                    }}
                />
            </>}
            <ConfirmActionModal
                visible={!!pendingConfirmation}
                header={pendingConfirmation?.header ?? ""}
                message={pendingConfirmation?.message ?? ""}
                description={pendingConfirmation?.description}
                alertType={pendingConfirmation?.alertType ?? "warning"}
                confirmLabel={pendingConfirmation?.confirmLabel ?? "Confirm"}
                onConfirm={() => pendingConfirmation?.onConfirm()}
                onCancel={() => setPendingConfirmation(null)}
            />
            <OperationInProgressNotification triggerCount={duplicateAttemptCount} />
        </>;
    };
}
